/**
 * Reorder Words Exercise (INT-CMP-004)
 * User taps words to arrange them in the correct sentence order.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useKeyboardShortcut, useNumberKeyShortcuts } from '../../hooks/useKeyboardShortcut';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface ReorderWordsExerciseProps {
  prompt: string;
  words: string[];
  correctOrder: string[];
  hint?: string;
  onComplete: (correct: boolean, userOrder: string[]) => void;
}

export const ReorderWordsExercise = React.memo(function ReorderWordsExercise({
  prompt,
  words,
  correctOrder,
  hint,
  onComplete,
}: ReorderWordsExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [availableWords, setAvailableWords] = useState<string[]>(words);
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleWordSelect = useCallback((word: string, index: number) => {
    if (submitted) return;
    setSelectedWords((prev) => [...prev, word]);
    setAvailableWords((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, [submitted]);

  const handleWordRemove = useCallback((word: string, index: number) => {
    if (submitted) return;
    setAvailableWords((prev) => [...prev, word]);
    setSelectedWords((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, [submitted]);

  const handleSubmit = useCallback(() => {
    const correct = selectedWords.length === correctOrder.length &&
      selectedWords.every((w, i) => w === correctOrder[i]);
    setIsCorrect(correct);
    setSubmitted(true);
    onComplete(correct, selectedWords);
  }, [selectedWords, correctOrder, onComplete]);

  const handleReset = useCallback(() => {
    setSelectedWords([]);
    setAvailableWords(words);
    setSubmitted(false);
    setIsCorrect(false);
  }, [words]);

  const handleAvailableWordByIndex = useCallback((i: number) => {
    if (i < availableWords.length) {
      handleWordSelect(availableWords[i], i);
    }
  }, [availableWords, handleWordSelect]);

  const removeLastWord = useCallback(() => {
    if (selectedWords.length === 0 || submitted) return;
    const lastIdx = selectedWords.length - 1;
    handleWordRemove(selectedWords[lastIdx], lastIdx);
  }, [selectedWords, submitted, handleWordRemove]);

  // Keyboard shortcuts: number keys select available words, Backspace removes last, Enter submits (web only, no-op on native)
  useNumberKeyShortcuts(availableWords.length, handleAvailableWordByIndex, !submitted);
  useKeyboardShortcut('Backspace', removeLastWord, { enabled: !submitted && selectedWords.length > 0 });
  useKeyboardShortcut('Enter', handleSubmit, { enabled: !submitted && selectedWords.length > 0 });

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.text, ...ffs }}
        >
          {prompt}
        </Text>

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Selected words area */}
        <Box
          p="$3"
          rounded="$md"
          style={{
            backgroundColor: t.inputBg,
            borderWidth: 1,
            borderColor: submitted ? (isCorrect ? '#059669' : '#DC2626') : t.border,
            borderStyle: selectedWords.length === 0 ? 'dashed' : 'solid',
            minHeight: 48,
          }}
        >
          {selectedWords.length === 0 ? (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Tap words below to build the sentence
            </Text>
          ) : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {selectedWords.map((word, idx) => (
                <TouchableOpacity
                  key={`selected-${idx}`}
                  testID={`reorder-selected-${idx}`}
                  accessibilityLabel={`Remove ${word}`}
                  accessibilityRole="button"
                  onPress={() => handleWordRemove(word, idx)}
                  disabled={submitted}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$3"
                    py="$1"
                    rounded="$md"
                    style={{
                      backgroundColor: submitted
                        ? (isCorrect ? '#D1FAE5' : '#FEE2E2')
                        : colors.primary,
                    }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '500',
                        color: submitted ? (isCorrect ? '#065F46' : '#991B1B') : '#FFFFFF',
                        ...ffs,
                      }}
                    >
                      {word}
                    </Text>
                  </Box>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </Box>

        {/* Available words */}
        {availableWords.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {availableWords.map((word, idx) => (
              <TouchableOpacity
                key={`available-${idx}`}
                testID={`reorder-available-${idx}`}
                accessibilityLabel={`Select ${word}`}
                accessibilityRole="button"
                onPress={() => handleWordSelect(word, idx)}
                disabled={submitted}
                activeOpacity={0.7}
              >
                <Box
                  px="$3"
                  py="$1"
                  rounded="$md"
                  style={{
                    backgroundColor: t.isDark ? '#374151' : '#F3F4F6',
                    borderWidth: 1,
                    borderColor: t.border,
                  }}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, fontWeight: '500', color: t.text, ...ffs }}
                  >
                    {word}
                  </Text>
                </Box>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Submit / Reset buttons */}
        {!submitted && selectedWords.length > 0 && (
          <HStack space="sm">
            <TouchableOpacity
              testID="reorder-reset"
              accessibilityLabel="Reset word order"
              accessibilityRole="button"
              onPress={handleReset}
              activeOpacity={0.7}
              style={{ flex: 1 }}
            >
              <Box
                py="$2"
                rounded="$md"
                alignItems="center"
                style={{ backgroundColor: t.isDark ? '#374151' : '#F3F4F6' }}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.textSecondary, ...ffs }}
                >
                  Reset
                </Text>
              </Box>
            </TouchableOpacity>
            <TouchableOpacity
              testID="reorder-submit"
              accessibilityLabel="Check answer"
              accessibilityRole="button"
              onPress={handleSubmit}
              activeOpacity={0.7}
              style={{ flex: 2 }}
            >
              <Box
                py="$2"
                rounded="$md"
                alignItems="center"
                style={{ backgroundColor: colors.primary }}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
                >
                  Check
                </Text>
              </Box>
            </TouchableOpacity>
          </HStack>
        )}

        {/* Result feedback */}
        {submitted && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: isCorrect ? '#D1FAE5' : '#FEE2E2' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: isCorrect ? '#065F46' : '#991B1B',
                ...ffs,
              }}
            >
              {isCorrect ? '✓ Correct!' : `✗ Correct order: ${correctOrder.join(' ')}`}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
